'use client';

import { useState, useEffect } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';

// Static category data - no need to fetch from WordPress
const STATIC_CATEGORIES = [
  { id: 'all', name: 'All', slug: 'all' },
  { id: 'image-gen', name: 'Image Generation', slug: 'image-generation' },
  { id: 'music-audio', name: 'Music / Audio', slug: 'music-audio' },
  { id: 'website-app', name: 'Website / App', slug: 'website-app' },
  { id: 'graphic-design', name: 'Graphic Design', slug: 'graphic-design' },
  { id: 'text-copy', name: 'Text / Copywriting', slug: 'text-copywriting' },
  { id: '3d', name: '3D', slug: '3d' },
  { id: 'video-film', name: 'Video / Film', slug: 'video-film' },
  { id: 'presentation', name: 'Presentation', slug: 'presentation' },
  { id: 'animation', name: 'Animation', slug: 'animation' },
  { id: 'coding', name: 'Coding', slug: 'coding' }
];


interface ToolsCategoriesStaticProps {
  onCategoryChange?: (category: string) => void;
  onSearchChange?: (query: string) => void;
}

export default function ToolsCategoriesStatic({ onCategoryChange, onSearchChange }: ToolsCategoriesStaticProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const categoryParam = searchParams.get('category');
  
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  
  // Initialize from URL params
  useEffect(() => {
    const category = STATIC_CATEGORIES.find(c => c.slug === categoryParam);
    if (category) {
      setSelectedCategory(category.name);
    }
  }, [categoryParam]);
  
  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category);
    
    // Update URL
    const url = new URL(window.location.href);
    if (category === 'All') {
      url.searchParams.delete('category');
    } else {
      const cat = STATIC_CATEGORIES.find(c => c.name === category);
      if (cat) {
        url.searchParams.set('category', cat.slug);
      }
    }
    router.push(url.pathname + url.search, { scroll: false });
    
    // Notify parent
    onCategoryChange?.(category);
  };
  
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Update URL
    const url = new URL(window.location.href);
    if (query.trim()) {
      url.searchParams.set('search', query);
    } else {
      url.searchParams.delete('search');
    }
    router.push(url.pathname + url.search, { scroll: false });
    
    // Notify parent
    onSearchChange?.(query);
  };
  
  return (
    <>
      {/* Search Bar Section */}
      <div className="w-full flex justify-center">
        <section className="relative w-full max-w-[1440px] bg-white px-6 sm:px-8 lg:px-12 pb-12">
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search AI tools..."
                className="w-full px-12 py-4 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-black focus:border-black text-gray-800 placeholder-gray-500"
              />
              <svg
                className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </section>
      </div>
      
      {/* Categories Section */}
      <div className="w-full flex justify-center">
        <section className="relative w-full max-w-[1440px] bg-white border-t border-black/10 px-6 sm:px-8 lg:px-12 py-8">
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
            {STATIC_CATEGORIES.map((category) => (
              <button
                key={category.id}
                onClick={() => handleCategoryClick(category.name)}
                className={`px-4 py-2 font-ibm-plex-sans-condensed text-[12px] font-medium tracking-[1.2px] uppercase whitespace-nowrap transition-all border ${
                  selectedCategory === category.name
                    ? 'bg-black text-white border-black'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-black hover:bg-gray-50'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}